import React, { useState, useEffect, useCallback, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Import images
import image1 from "../../../../assets/images/gallery/1.webp";
import image3 from "../../../../assets/images/gallery/3.webp";
import image4 from "../../../../assets/images/gallery/4.webp";
import image5 from "../../../../assets/images/gallery/5.webp";
import image6 from "../../../../assets/images/gallery/6.webp";

const images = [
  { src: image4, alt: "Gallery Image 4" },
  { src: image1, alt: "Gallery Image 1" },
  { src: image3, alt: "Gallery Image 3" },
  { src: image5, alt: "Gallery Image 5" },
  { src: image6, alt: "Gallery Image 6" },
];

const GallerySection: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length,
    );
  }, []);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setDragOffset(0);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !containerRef.current) return;

    const currentTouch = e.targetTouches[0].clientX;
    const diff = currentTouch - touchStart;
    const containerWidth = containerRef.current.offsetWidth;
    const dragPercentage = (diff / containerWidth) * 100;

    setDragOffset(dragPercentage);
    setTouchEnd(currentTouch);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !containerRef.current) return;

    const containerWidth = containerRef.current.offsetWidth;
    const distance = touchEnd - touchStart;
    const threshold = containerWidth * 0.2;

    if (Math.abs(distance) > threshold) {
      if (distance > 0) {
        prevSlide();
      } else {
        nextSlide();
      }
    }

    setIsDragging(false);
    setDragOffset(0);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || isDragging) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, currentIndex, nextSlide, isDragging]);

  // Calculate the transform value for smooth sliding
  const getTransformValue = () => {
    const baseTransform = -currentIndex * 100;
    const dragTransform = isDragging ? dragOffset : 0;
    return `translateX(calc(${baseTransform}% + ${dragTransform}%))`;
  };

  const handleArrowClick = (direction: "next" | "prev") => {
    setIsDragging(false);
    setDragOffset(0);
    if (direction === "next") {
      nextSlide();
    } else {
      prevSlide();
    }
  };

  return (
    <div className="w-full overflow-hidden">
      <div
        ref={containerRef}
        className="relative w-full h-[40vh] md:h-[100vh] relative overflow-hidden group touch-pan-y"
        onMouseEnter={() => setIsAutoPlaying(false)}
        onMouseLeave={() => setIsAutoPlaying(true)}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Image Container */}
        <div
          ref={sliderRef}
          className="flex h-full will-change-transform"
          style={{
            transform: getTransformValue(),
            transition: isDragging
              ? "none"
              : "transform 500ms cubic-bezier(0.4, 0, 0.2, 1)",
            touchAction: "pan-x pinch-zoom",
          }}
        >
          {images.map((image, index) => (
            <div key={index} className="relative w-full h-full flex-shrink-0">
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover select-none pointer-events-none"
                style={{ objectFit: "cover", objectPosition: "top" }}
                draggable="false"
              />
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </div>
          ))}
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => handleArrowClick("prev")}
          className="absolute left-6 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 hover:-translate-x-1 focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>

        <button
          onClick={() => handleArrowClick("next")}
          className="absolute right-6 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110 hover:translate-x-1 focus:outline-none focus:ring-2 focus:ring-white/50"
          aria-label="Next image"
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        {/* Dot Indicators */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex space-x-3">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${
                index === currentIndex
                  ? "bg-white scale-125"
                  : "bg-white/50 hover:bg-white/75"
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-white/20">
          <div
            className="h-full bg-white transition-all duration-700 ease-out"
            style={{ width: `${((currentIndex + 1) / images.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default GallerySection;
